import { IKnmResultDecision248 } from 'APIPackage/KnmModule/interfaces/IKnmResultDecision248';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { convertFormDateValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateValueToKnmDataDate';
import { convertKnmDocumentAttachmentsFormValuesToKnmData } from 'KnmPackage/MainModule/common/convertKnmDocumentAttachmentsFormValuesToKnmData';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IKnmActFamiliarizationNotifications } from 'APIPackage/KnmModule/interfaces/IKnmActFamiliarizationNotifications';
import { store } from 'CorePackage/MainModule/common/store';
import { getDirtyResultDecisionGuid } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { convertFormDateTimeValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateTimeValueToKnmDataDate';
import { convertSubjectFormValuesToKnmData } from 'KnmPackage/KnmSubjectModule/common/convertSubjectFormValuesToKnmData';

import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';
import { convertInspectorsFormValuesToKnmDataOld } from './convertInspectorsFormValuesToKnmDataOld';
import { convertViolatedRequirementsFormValuesToKnmData } from './convertViolatedRequirementsFormValuesToKnmData';
import { convertKnmOkvedsFormValueToKnmData } from '../../../KnmPackage/MainModule/common/convertKnmOkvedsFormValueToKnmData';
import { KnmResponsibleTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmResponsibleTypeCodesEnum';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';

export const convertResultDecisionsFormValueToKnmData = (
  formResultDecisions: IKnmErknmFormResultDecision[] | undefined,
  created: string | undefined,
  isDraft: boolean | undefined,
): IKnmResultDecision248[] | null => {
  if (!formResultDecisions) {
    return null;
  }

  const state = store.getState();
  const settings = getAdminFetchSettings(state);
  const dirtyResultDecisionGuid = getDirtyResultDecisionGuid(state);

  const isShow4127 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM,
  );

  return formResultDecisions.map(item => {
    const resultDecisionData: IKnmResultDecision248 = {
      isNew: isDraft ? item.isNew : false,
      dateCreate: item.dateCreate || null,
      dateDecision: convertFormDateTimeValueToKnmDataDate(item.dateDecision, true) || null,
      dateResolvedByAgreement: convertFormDateTimeValueToKnmDataDate(item.dateResolvedByAgreement) || null,
      document: !item.document
        ? null
        : {
            active: item.document?.active || null,
            attachments: convertKnmDocumentAttachmentsFormValuesToKnmData(item.document?.attachments),
            correlationToken: item.document?.correlationToken || null,
            description: item.document?.description || null,
            guid: item.document?.guid || null,
            type: item.document?.type || null,
          },
      fioSigner: item.fioSigner || null,
      guid: item.guid || null,
      injunction:
        (item.injunction && {
          dateResolved: convertFormDateValueToKnmDataDate(item.injunction.dateResolved),
          note: item.injunction.note || null,
          noteReactQuillEditor: handleReactQuillDeltaValue(
            item.injunction.noteReactQuillEditor || item.injunction.note,
          ),
          guid: item.injunction.guid || null,
          dateCreate: item.injunction.dateCreate || null,
        }) ||
        null,
      inspectors: convertInspectorsFormValuesToKnmDataOld(item.inspectors),
      isProsecution: typeof item.isProsecution?.value === 'boolean' ? item.isProsecution.value : null,
      isProsecutionProtocol: null,
      isResponse: null,
      kindDecision: item.kindDecision ? deleteObjectProperties(item.kindDecision, ['label', 'value']) : null,
      founder: (item.founder && convertSubjectFormValuesToKnmData(item.founder)) || null,
      subjectType: item.subjectTypeFounder ? item.subjectTypeFounder.value : null,
      links: !item.links
        ? null
        : item.links.map(link => {
            return {
              guid: link.guid,
              erpId: link.erpId,
              id: link.id,
            };
          }),
      noteLawsuits:
        item.noteLawsuits?.map(noteLawsuit => ({
          value: noteLawsuit.value || null,
          valueReactQuillEditor: handleReactQuillDeltaValue(noteLawsuit.valueReactQuillEditor || noteLawsuit.value),
          guid: noteLawsuit.guid || null,
          dateCreate: noteLawsuit.dateCreate || null,
        })) || null,
      numberDecision: item.numberDecision || null,
      protocol: {
        active: item.protocol?.active || null,
        attachments: convertKnmDocumentAttachmentsFormValuesToKnmData(item.protocol?.attachments),
        correlationToken: item.protocol?.correlationToken || null,
        description: item.protocol?.description || null,
        guid: item.protocol?.guid || null,
        type: item.protocol?.type || null,
      },
      punishmentNature: item.punishmentNature ? item.punishmentNature.value : null,
      responsibleEntities:
        item.responsibleEntities?.map(responsibleEntity => {
          return {
            subjectType: !isShow4127
              ? responsibleEntity?.subjectType || null
              : {
                  id: responsibleEntity.organization?.value?.subjectType?.id,
                  name: responsibleEntity.organization?.value?.subjectType?.label || null,
                  code: responsibleEntity.organization?.value?.subjectType?.code as KnmResponsibleTypeCodesEnum,
                },
            okveds: convertKnmOkvedsFormValueToKnmData(responsibleEntity.organization?.value?.okveds),
            locationAddress: responsibleEntity.organization?.value?.locationAddress || null,
            firstOrganizationName: responsibleEntity.organization?.value?.firstOrganizationName || null,
            workAddress: responsibleEntity.organization?.value?.workAddress || null,
            isSonko:
              typeof responsibleEntity.organization?.value?.isSonko === 'boolean'
                ? responsibleEntity.organization?.value?.isSonko
                : null,
            isPresentInItRegistry: responsibleEntity.organization?.value?.isPresentInItRegistry || null,
            structuresNPA: !responsibleEntity.structuresNPA
              ? null
              : responsibleEntity.structuresNPA.map(i => ({
                  value: i.value || null,
                  guid: i.guid || null,
                  dateCreate: i.dateCreate || null,
                })),
            factualAddress: responsibleEntity.organization?.value?.factualAddress || null,
            nkoForeignAgentsRegistryNumber:
              responsibleEntity.organization?.value?.nkoForeignAgentsRegistryNumber || null,
            nkoForeignAgentsRegistryInclusionDate:
              responsibleEntity.organization?.value?.nkoForeignAgentsRegistryInclusionDate || null,
            nkoForeignAgentsRegistryExclusionDate:
              responsibleEntity.organization?.value?.nkoForeignAgentsRegistryExclusionDate || null,
            isNko: !!responsibleEntity.organization?.value?.nkoForeignAgentsRegistryInclusionDate,
            addressFns: responsibleEntity.organization?.value?.addressFns || null,
            country: responsibleEntity.organization?.value?.country || null,
            kpp: responsibleEntity.organization?.value?.kpp || null,
            inn: (isShow4127 ? responsibleEntity.organization?.value?.inn : responsibleEntity.inn) || null,
            ogrn: (isShow4127 ? responsibleEntity.organization?.value?.ogrn : responsibleEntity.ogrn) || null,
            snils: (isShow4127 ? responsibleEntity.organization?.value?.snils : responsibleEntity.snils) || null,
            fullName: responsibleEntity.fullName || null,
            position:
              (isShow4127 ? responsibleEntity.organization?.value?.position : responsibleEntity.position) || null,
            organizationName:
              (isShow4127
                ? responsibleEntity.organization?.value?.organizationName
                : responsibleEntity.organizationName) || null,
            guid: responsibleEntity.guid || null,
            mspCode: responsibleEntity.organization?.value?.mspCode || null,
            isAutocomplete: null,
            epguNotifications: null,
            isFiz:
              typeof responsibleEntity.organization?.value?.isFiz === 'boolean'
                ? responsibleEntity.organization?.value?.isFiz
                : null,
            checkSubjectResult: responsibleEntity.organization?.value?.checkSubjectResult || null,
            isDocumentOrganization: responsibleEntity.organization?.value?.isDocumentOrganization || null,
            rafpNza: responsibleEntity.organization?.value?.rafpNza || null,
            ervkId: responsibleEntity.organization?.value?.ervkId || null,
            document: responsibleEntity.organization?.value?.document || null,
            smevRequestStatus: responsibleEntity.organization?.value?.smevRequestStatus || null,
            isForeignSubjectAgencyNotRegistredInRf:
              responsibleEntity.organization?.value?.isForeignSubjectAgencyNotRegistredInRf || null,
            dateCreate: responsibleEntity.organization?.value?.dateCreate || null,
            punishmentAmount: Number(responsibleEntity.punishmentAmount?.replaceAll(' ', '')) || null,
            punishmentNote: responsibleEntity.punishmentNote || null,
            punishmentUins: responsibleEntity.punishmentUins ? responsibleEntity.punishmentUins.filter(i => i) : null,
            punishmentAmountMeasure: responsibleEntity.punishmentAmountMeasure || null,
            punishmentTerm: responsibleEntity.punishmentTerm || null,
            responsibilityType:
              (responsibleEntity.responsibilityType && {
                ...deleteObjectProperties(responsibleEntity.responsibilityType, ['label', 'value']),
              }) ||
              null,
            ervkControlKindNpaUnit: responsibleEntity.ervkControlKindNpaUnit || null,
            inspectionResults: responsibleEntity.inspectionResults,
          };
        }) || null,
      status: item.status || null,
      agreementStatus: item.agreementStatus || null,
      subjectDecision: {
        inn: item.subjectDecision?.inn || null,
        ogrn: item.subjectDecision?.ogrn || null,
        organizationName: item.subjectDecision?.organizationName || null,
        guid: item.subjectDecision?.guid || null,
        dateCreate: item.subjectDecision?.dateCreate || null,
      },
      subjectIsAutocomplete: typeof item.subjectIsAutocomplete === 'boolean' ? item.subjectIsAutocomplete : true,
      titleSigner: item?.titleSigner?.value || null,
      violations: convertViolatedRequirementsFormValuesToKnmData(item.violatedMandatoryRequirements, isDraft),
      actGuid: item.act?.id || null,
      paperless: item.paperless || null,
      signed: dirtyResultDecisionGuid && dirtyResultDecisionGuid === item.guid ? false : item.signed ?? null,
      extractVersion: item.extractVersion || null,
      familiarizationNotifications:
        item?.familiarizationNotifications?.map(x => {
          const notification: IKnmActFamiliarizationNotifications = {
            noticeDate: convertFormDateValueToKnmDataDate(x.noticeDate),
            knmNoticeType: x.knmNoticeType || null,
            note: x.note || null,
            guid: x.listId,
            isNew: x.isNew,
          };

          return notification;
        }) || null,
      firstExtractDateTime: convertFormDateTimeValueToKnmDataDate(item?.firstExtractDateTime) || null,
      systemSignatureUsed: item.systemSignatureUsed || false,
      systemSignatureDateTime: item.systemSignatureDateTime || null,
      systemSignatureMnemonic: item.systemSignatureMnemonic || null,
      systemSignatureTitle: item.systemSignatureTitle || null,
      cancelComment: item.cancelComment || null,
      cancelFioSigner: item.cancelFioSigner ? item.cancelFioSigner : state.knm.prescribeDataForSign?.fioSigner || null,
      cancelTitleSigner: item.cancelTitleSigner?.value
        ? item.cancelTitleSigner?.value
        : state.knm.prescribeDataForSign?.signerPosition?.value || null,
      cancelDocument: convertKnmDocumentAttachmentsFormValuesToKnmData([item.cancelDocument])?.pop() || null,
      isCancelSigned: item.isCancelSigned || null,
      dateAdded: item.dateAdded || null,
    };

    return resultDecisionData;
  });
};
